import { FC, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';

import RenderInput from '../../../../components/RenderInput';
import GeneralModal from '../../../../components/GeneralModal';
import { documentTypesInitialsEnum } from '../../../../api/vouchers/helpers';
import ChangeRate from '../../ChangeRate/ChangeRate';
import { Context as ExchangeRatesContext } from '../../../../api/exchangeRates/context';
import { Context as ClientsContext } from '../../../../api/clients/context';
import { Context as CurrenciesContext } from '../../../../api/currencies/context';
import { Client } from '../../../../api/clients/types';
import { Currency } from '../../../../api/currencies/types';
import {
    resolveDocumentLocalRate,
    resolveSourceDocumentRateForForm,
    sameCurrency
} from '../../../../utils/currencyMath';

type RateControlBoxProps = {
    showRateInput?: boolean;
    title?: string;
    currencyId?: string;
    /** Default date for the rate lookup — e.g., the issue date of a PRQ/Invoice. Defaults to today. */
    defaultRateDate?: string | Date;
    /** Optional label shown above the rate (e.g., "Rate of PRQ #123456") for context */
    rateSourceLabel?: string;
    /** Source document — if provided, defaultRateDate and rateSourceLabel are auto-derived */
    documentGenerationData?: any;
    /** External date override (e.g., from payment method value date). When changed, syncs the rate date picker. */
    rateDateOverride?: string | Date | null;
    /** Force default behavior to start from today's date and ignore external/source defaults. */
    forceTodayDefault?: boolean;
    /** Disable all rate controls (rate input, date picker, Change Rate button). */
    disabled?: boolean;
    /** Disable only the Rate Date picker (e.g. the date is driven externally). */
    disableRateDate?: boolean;
    /** Tooltip explaining why the Rate Date picker is disabled. */
    rateDateDisabledTooltip?: string;
};

// RenderInput is untyped JSX, so TS infers its props from the destructuring
// defaults (`handleChangeCallback = null`, a `field` without `value`) and
// rejects every real callback. Type it loosely here.
const TypedRenderInput = RenderInput as unknown as FC<Record<string, any>>;

const toDateInputString = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const parseDateLike = (value: string | Date | null | undefined): Date | null => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    const asNative = new Date(value);
    if (!Number.isNaN(asNative.getTime())) return asNative;
    const dmy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
    if (!dmy) return null;
    const dd = Number(dmy[1]);
    const mm = Number(dmy[2]);
    const yyyy = Number(dmy[3]);
    const parsed = new Date(yyyy, mm - 1, dd);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const RateControlBox: FC<RateControlBoxProps> = ({
    showRateInput = true,
    title = 'Rate Settings',
    currencyId: currencyIdProp,
    defaultRateDate,
    rateSourceLabel,
    documentGenerationData,
    rateDateOverride,
    forceTodayDefault = false,
    disabled = false,
    disableRateDate = false,
    rateDateDisabledTooltip = ''
}) => {
    const { setFieldValue, values } = useFormikContext<any>();
    const { getConvertRate, getRateForDate, get: getExchangeRates } = useContext(ExchangeRatesContext);
    const clientsContext = useContext(ClientsContext);
    const currenciesContext = useContext(CurrenciesContext);
    const localCurrency: string = (clientsContext.selected as Client)?.currencySettings
        ?.localCurrency as string;
    const primaryCurrency: string = (clientsContext.selected as Client)?.currencySettings
        ?.primaryCurrency as string;

    // Auto-derive date and label from documentGenerationData if provided
    const autoDate =
        defaultRateDate ??
        documentGenerationData?.issueDate ??
        documentGenerationData?.createdAt ??
        documentGenerationData?.info?.dueDate;
    // Source doc's rate, inherited so a PRQ/Remit/Receipt uses the SOURCE
    // document's rate instead of re-fetching today's market rate.
    //
    // Which rate that is depends on the source's own currency, and matches what
    // this box is labelled below: local-per-document-unit for a foreign source,
    // but local→PRIMARY (₪ per $) for a LOCAL-currency source, where
    // document→local is a tautological 1. Resolving only the first question is
    // what made Prepare Payment show "Rate ₪ → $ = 1" on a ₪152 voucher that
    // stores 2.9798 in `info.rate`.
    const originalRate: number | undefined = useMemo(
        () =>
            resolveSourceDocumentRateForForm(
                {
                    ...(documentGenerationData?.info || {}),
                    // Some callers hang localRate off the document rather than
                    // its info.
                    localRate:
                        documentGenerationData?.info?.localRate ?? documentGenerationData?.localRate
                },
                {
                    localId: localCurrency,
                    primaryId: primaryCurrency,
                    // Stored rates only. A market fallback here would count as
                    // "inherited" below and suppress the date-based lookup,
                    // silently swapping the source's issue-date rate for today's.
                    getConvertRate: () => null
                }
            ),
        [documentGenerationData, localCurrency, primaryCurrency]
    );
    const showOriginalRate = originalRate && Number(originalRate) > 0;
    const autoLabel =
        rateSourceLabel ??
        (documentGenerationData?.type && documentGenerationData?.number
            ? `${(documentTypesInitialsEnum as any)[documentGenerationData.type] || documentGenerationData.type} #${documentGenerationData.number}${
                  showOriginalRate ? ` · ${Math.round(Number(originalRate) * 10000) / 10000}` : ''
              }`
            : undefined);
    /**
     * The IMMUTABLE origin: what the source document actually obliges us to pay,
     * expressed in local (₪) currency.
     *
     * Switching the display currency does not change what is owed, so every
     * conversion derives from this anchor rather than from the previously
     * selected currency. Chaining off the previous selection is what let a
     * ¥40,000 PRQ come back as "$40,000" instead of $244 — and it also
     * accumulated rounding drift on every switch.
     */
    const origin = useMemo(() => {
        const info = documentGenerationData?.info;
        const currencyId =
            typeof info?.currency === 'object' ? info?.currency?._id : info?.currency;
        const amount = Number(info?.amount) || 0;
        const localRate = resolveDocumentLocalRate({
            currencyId,
            localId: localCurrency,
            storedLocalRate: info?.localRate,
            amount,
            amountLocal: info?.amountLocal
        });
        return {
            currencyId,
            amount,
            localRate: localRate || 0,
            local: localRate && amount > 0 ? amount * localRate : 0
        };
    }, [documentGenerationData, localCurrency]);

    const [changeRateOpen, setChangeRateOpen] = useState<boolean>(false);
    const prevCurrencyRef = useRef<string | null>(null);
    /**
     * Which currency the value in `values.rate` actually describes.
     *
     * A rate — inherited from the source document, fetched, or typed by the
     * user — is only meaningful for one currency. Without this, an inherited
     * JPY rate stayed "locked" and was reused as the divisor for USD.
     */
    const rateOwnerRef = useRef<string | null>(null);
    // Date of the rate to fetch — defaults to source date unless forced to today.
    const [rateDate, setRateDate] = useState<string>(() => {
        if (forceTodayDefault) return toDateInputString(new Date());
        const parsed = parseDateLike(autoDate);
        return toDateInputString(parsed || new Date());
    });
    // Once user manually edits the rate, stop auto-overwriting on currency/date changes
    const isRateCustomizedRef = useRef(false);
    /**
     * A ₪ amount this box converted from a debt held in the PRIMARY currency
     * ($5,303.61 → ₪16,130.94), with the primary amount it stands for. While
     * `values.amount` still equals `local`, a rate-date change re-converts it as
     * `primary × newRate` — the debt is dollars, so its shekel value follows the
     * date's rate. Null whenever that does not hold: a debt in ₪ (round-tripping
     * ₪ → $ → ₪ must not move it) or in a third currency (EUR is not rescaled by
     * the dollar rate). A typed amount stops matching `local` and is left alone.
     */
    const autoLocalAmountRef = useRef<{ local: number; primary: number } | null>(null);
    // The currency the box opened in — for a receipt, the travel file's, which is
    // the currency of the debt when there is no source document.
    const initialCurrencyRef = useRef<string | null>(null);
    // Latest render's values and rate date, for async callbacks that must not act
    // on the closure they were created in.
    const valuesRef = useRef(values);
    valuesRef.current = values;
    const rateDateRef = useRef(rateDate);
    rateDateRef.current = rateDate;

    // Use the prop if provided, otherwise derive from Formik values
    // Handle both object ({_id, code, ...}) and string ID formats
    const currId: string = useMemo(() => {
        const raw = currencyIdProp ?? values.currency;
        return typeof raw === 'object' ? raw?._id : raw;
    }, [currencyIdProp, values.currency]);

    // Rule (matches the Accounting tab): for a LOCAL-currency document the
    // object↔local rate is a meaningless 1, so the rate we show/fetch is the
    // local↔PRIMARY (ILS↔USD) rate — and it still updates with the date.
    const isLocalDoc = !!currId && String(currId) === String(localCurrency);
    const rateCurrencyId: string = isLocalDoc && primaryCurrency ? primaryCurrency : currId;

    const currencyOptions = useMemo(
        () => currenciesContext.dataAsArray || [],
        [currenciesContext.dataAsArray]
    );

    const documentCurrency: Currency = useMemo(() => {
        return currencyOptions.find((curr: Currency) => curr._id === currId) || values.currency;
    }, [currId, currencyOptions, values.currency]);

    const localCurrencySymbol: string = useMemo(() => {
        const cur = currencyOptions.find((c: Currency) => c._id === localCurrency);
        return cur?.symbol || cur?.code || '';
    }, [currencyOptions, localCurrency]);

    // Symbol of the rate's quote currency (PRIMARY for a local doc, else the
    // document currency) — used in the "Rate ₪ → $" label.
    const rateQuoteSymbol: string = useMemo(() => {
        const found = currencyOptions.find((c: Currency) => c._id === rateCurrencyId);
        return found?.symbol || found?.code || '';
    }, [rateCurrencyId, currencyOptions]);

    // Sync rateDate when an external override (e.g. payment value date) changes
    useEffect(() => {
        if (forceTodayDefault) return;
        if (rateDateOverride == null) return;
        const parsedOverride = parseDateLike(rateDateOverride);
        if (!parsedOverride) return;
        const overrideStr = toDateInputString(parsedOverride);
        if (overrideStr !== rateDate) {
            setRateDate(overrideStr); // triggers the rateDate effect below which fetches the rate
        }
    }, [forceTodayDefault, rateDateOverride]); // eslint-disable-line react-hooks/exhaustive-deps

    // In today-default mode, set today's date as baseline when the mode is entered.
    useEffect(() => {
        if (!forceTodayDefault) return;
        const today = toDateInputString(new Date());
        setRateDate((prev) => (prev === today ? prev : today));
    }, [forceTodayDefault]);

    // Update rate when currency changes or on initial mount
    const roundRate = (n: number) => Math.round(n * 1000000) / 1000000; // 6 decimal places for precision

    // Helper: get rate for a currency at a given date — uses in-memory cache, falls back to API
    const fetchRateForDate = useCallback(
        async (curId: string, dateStr: string): Promise<number | null> => {
            if (!localCurrency) return null;
            // For yyyy-mm-dd input, query end-of-day so "selected date" includes that whole day.
            const ymdMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
            const dateMs = ymdMatch
                ? new Date(
                      Number(ymdMatch[1]),
                      Number(ymdMatch[2]) - 1,
                      Number(ymdMatch[3]),
                      23,
                      59,
                      59,
                      999
                  ).getTime()
                : new Date(dateStr).getTime();
            if (Number.isNaN(dateMs)) return null;
            // Try in-memory cache first (instant, no API call)
            const cached = getRateForDate(curId, localCurrency, dateMs);
            if (cached?.exchangeRateSell) return cached.exchangeRateSell;
            // Fallback to API for edge cases (e.g., allRates not loaded yet)
            const rates = await getExchangeRates({ date: dateMs }, undefined, false);
            const rate = getConvertRate(curId, localCurrency, rates);
            const byDate = (rate as { exchangeRateSell: number } | null)?.exchangeRateSell || 0;
            if (byDate > 0) return byDate;
            // Final fallback: latest known (todayData), never force 1.
            const latest = getConvertRate(curId, localCurrency);
            const latestSell = (latest as { exchangeRateSell: number } | null)?.exchangeRateSell || 0;
            return latestSell > 0 ? latestSell : null;
        },
        [getRateForDate, getExchangeRates, getConvertRate, localCurrency]
    );

    useEffect(() => {
        if (!currId) return;
        if (prevCurrencyRef.current === null) {
            prevCurrencyRef.current = currId;
            initialCurrencyRef.current = currId;
            // If the form was seeded with a rate inherited from a source
            // document (e.g. PRQ from a Voucher, Remit from a PRQ), respect
            // it — don't overwrite with today's exchange rate on mount. It is
            // authoritative only for the currency it came from, which is what
            // rateOwnerRef records.
            const hasInheritedRate = !forceTodayDefault && (originalRate ?? 0) > 0;
            if (hasInheritedRate) {
                isRateCustomizedRef.current = true;
                // For a LOCAL-currency source the inherited number is the
                // ₪→$ rate, so its owner is the PRIMARY currency — recording
                // the local currency instead would make a round-trip away and
                // back to ₪ refetch the market rate over the inherited one.
                rateOwnerRef.current = sameCurrency(origin.currencyId, localCurrency)
                    ? primaryCurrency ?? origin.currencyId ?? currId
                    : origin.currencyId ?? currId;
                return;
            }
            if (
                rateCurrencyId &&
                String(rateCurrencyId) !== String(localCurrency) &&
                !isRateCustomizedRef.current
            ) {
                fetchRateForDate(rateCurrencyId, rateDate).then((newRate) => {
                    const resolvedRate = Number(newRate) || 0;
                    if (!isRateCustomizedRef.current && resolvedRate > 0) {
                        setFieldValue('rate', roundRate(resolvedRate));
                        rateOwnerRef.current = rateCurrencyId;
                    }
                });
            }
            return;
        }

        if (currId === prevCurrencyRef.current) return;

        const prevCurrency = prevCurrencyRef.current;
        prevCurrencyRef.current = currId;
        autoLocalAmountRef.current = null;

        // The anchor is the obligation's LOCAL (₪) value. Prefer the origin
        // document; a standalone document with no source falls back to the live
        // amount×rate pair, which is the old behaviour.
        const wasLocal = String(prevCurrency) === String(localCurrency);
        const liveLocal = (Number(values.amount) || 0) * (wasLocal ? 1 : Number(values.rate) || 1);
        // May legitimately be 0 — a document started from Issue Document opens
        // at 0.00 with no source. That must only skip the AMOUNT conversion;
        // the rate still has to move to the newly-selected currency, or the box
        // keeps showing the previous currency's rate under the new label.
        const anchorLocal = origin.local > 0 ? origin.local : liveLocal;

        // Target is the LOCAL currency. Checked before the origin case below,
        // because for a local-currency origin `origin.localRate` is 1 — and 1 is
        // one of the two wrong answers here.
        if (String(currId) === String(localCurrency)) {
            const localAmount =
                origin.local > 0 && sameCurrency(currId, origin.currencyId)
                    ? origin.amount
                    : anchorLocal;
            if (localAmount > 0) {
                const roundedLocal = Math.round(localAmount * 100) / 100;
                setFieldValue('amount', roundedLocal);
                // Follows the rate date only for a debt in the primary currency,
                // converted from it just now at the rate still in `values.rate`.
                const debtCurrency = origin.local > 0 ? origin.currencyId : initialCurrencyRef.current;
                const fromRate = Number(values.rate) || 0;
                console.log('[RateControlBox] converted into local', {
                    roundedLocal,
                    fromRate,
                    prevCurrency,
                    debtCurrency,
                    primaryCurrency,
                    originLocal: origin.local,
                    originCurrency: origin.currencyId
                });
                if (
                    primaryCurrency &&
                    sameCurrency(debtCurrency, primaryCurrency) &&
                    sameCurrency(prevCurrency, primaryCurrency) &&
                    fromRate > 0
                ) {
                    // With a primary-currency source document its own amount IS
                    // the debt; local ÷ rate would mix the source's rate with
                    // whatever date's rate the form holds now.
                    const primaryAmount =
                        origin.local > 0 && sameCurrency(origin.currencyId, primaryCurrency)
                            ? origin.amount
                            : localAmount / fromRate;
                    autoLocalAmountRef.current = { local: roundedLocal, primary: primaryAmount };
                }
            }

            // For a LOCAL document `rate` means local-per-PRIMARY (₪ per $):
            // that's what the box is labelled (rateCurrencyId switches to the
            // primary currency above) and what amountToPrimary and the
            // amountObj save path both expect. Two wrong answers to avoid:
            //   1  — downstream computes amountObj = amount / rate, so a 1
            //        yields amountObj = amount but labelled in the object
            //        currency, off by an exchange rate.
            //   the PREVIOUS currency's rate — switching ¥ → ₪ used to leave
            //        "Rate ₪ → $" reading 0.0189, a yen rate on a shekel line.
            if (primaryCurrency && String(primaryCurrency) !== String(localCurrency)) {
                const alreadyPrimary =
                    String(rateOwnerRef.current || '') === String(primaryCurrency) &&
                    (Number(values.rate) || 0) > 0;
                if (!alreadyPrimary) {
                    fetchRateForDate(primaryCurrency, rateDate).then((newRate) => {
                        const resolvedRate = Number(newRate) || 0;
                        // Switched again while this was in flight — drop it.
                        if (prevCurrencyRef.current !== currId) return;
                        if (!(resolvedRate > 0)) return;
                        setFieldValue('rate', roundRate(resolvedRate));
                        rateOwnerRef.current = primaryCurrency;
                    });
                }
            }
            return;
        }

        // Back to the source document's own (foreign) currency — restore it
        // exactly, so round-tripping (¥ → $ → ₪ → ¥) returns the original figures.
        if (origin.local > 0 && sameCurrency(currId, origin.currencyId)) {
            setFieldValue('amount', Math.round(origin.amount * 100) / 100);
            setFieldValue('rate', roundRate(origin.localRate));
            rateOwnerRef.current = currId;
            return;
        }

        // Foreign and not the origin currency: whatever is in `values.rate`
        // belongs to a DIFFERENT currency, so it has to be re-fetched. Reuse it
        // only when it was fetched or typed for exactly this currency.
        const rateBelongsHere =
            String(rateOwnerRef.current || '') === String(currId) && (Number(values.rate) || 0) > 0;
        const ratePromise = rateBelongsHere
            ? Promise.resolve(Number(values.rate))
            : fetchRateForDate(currId, rateDate);

        ratePromise.then((newRate) => {
            const resolvedRate = Number(newRate) || 0;
            // The user switched again while this fetch was in flight; applying
            // it now would write one currency's rate under another's label.
            if (prevCurrencyRef.current !== currId) return;
            if (!(resolvedRate > 0)) return;
            if (anchorLocal > 0) {
                const convertedAmount = Math.round((anchorLocal / resolvedRate) * 100) / 100;
                setFieldValue('amount', convertedAmount);
                // Preserve the local total EXACTLY: the converted amount was
                // rounded to 2dp, so re-derive the rate from it — otherwise
                // convertedAmount * newRate drifts a cent (100 ILS → 99.99).
                setFieldValue(
                    'rate',
                    roundRate(convertedAmount > 0 ? anchorLocal / convertedAmount : resolvedRate)
                );
            } else {
                // Nothing to convert yet — just move the rate to this currency.
                setFieldValue('rate', roundRate(resolvedRate));
            }
            rateOwnerRef.current = currId;
        });
    }, [currId, localCurrency, forceTodayDefault]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Re-convert a ₪ amount this box converted from a dollar debt when the rate
     * changes — by date, by the picker, or typed by hand. $5,887.13 at 3.0609 is
     * ₪18,019.92; typing 3 makes it ₪17,661.39. Scaled from the stored dollar
     * amount, never from the previous rate. A ₪ amount the user typed (the
     * shekels actually received) no longer matches `auto.local` and is kept.
     */
    const reconvertAutoLocalAmount = useCallback(
        (newRate: number): void => {
            const auto = autoLocalAmountRef.current;
            const currentAmount = Number(valuesRef.current.amount) || 0;
            console.log('[RateControlBox] reconvert?', {
                newRate,
                isLocalDoc,
                auto,
                currentAmount,
                rawAmount: valuesRef.current.amount
            });
            if (!isLocalDoc || !auto || !(newRate > 0)) return;
            if (currentAmount !== auto.local) return;
            const rescaled = Math.round(auto.primary * newRate * 100) / 100;
            setFieldValue('amount', rescaled);
            autoLocalAmountRef.current = { local: rescaled, primary: auto.primary };
        },
        [isLocalDoc, setFieldValue]
    );

    // When rateDate changes, fetch rates for that date and update the rate
    const isInitialDateRef = useRef(true);
    useEffect(() => {
        if (isInitialDateRef.current) {
            isInitialDateRef.current = false;
            return; // skip on mount; initial rate set by the currency effect above
        }
        if (!rateCurrencyId || String(rateCurrencyId) === String(localCurrency)) return;
        // Date picker is an explicit user action — last write wins, so override any prior custom rate.
        // (For a local doc this fetches the PRIMARY↔local rate for the date.)
        const requestedDate = rateDate;
        fetchRateForDate(rateCurrencyId, requestedDate).then((newRate) => {
            // The date moved on while this was in flight — its own fetch owns
            // the rate now; applying this one would pair a stale rate with it.
            console.log('[RateControlBox] rate for date', {
                requestedDate,
                currentRateDate: rateDateRef.current,
                newRate
            });
            if (rateDateRef.current !== requestedDate) return;
            const resolvedRate = Number(newRate) || 0;
            if (!(resolvedRate > 0)) return;
            // $5,303.61 switched to ₪ at 27.09's 3.0609, then dated 24.09,
            // becomes ₪16,130.94 at 3.0415.
            reconvertAutoLocalAmount(resolvedRate);
            isRateCustomizedRef.current = false;
            setFieldValue('rate', roundRate(resolvedRate));
            rateOwnerRef.current = rateCurrencyId;
        });
    }, [rateDate]); // eslint-disable-line react-hooks/exhaustive-deps

    const setRate = useCallback(
        (newRate: number): void => {
            isRateCustomizedRef.current = true;
            reconvertAutoLocalAmount(newRate);
            setFieldValue('rate', roundRate(newRate));
            // A typed rate is for the currency on screen right now — switching
            // away must not carry it over as the divisor for the next one.
            rateOwnerRef.current = rateCurrencyId;
        },
        [setFieldValue, rateCurrencyId, reconvertAutoLocalAmount]
    );

    const setAmount = useCallback(
        (newAmount: number): void => {
            setFieldValue('amount', Math.round(newAmount * 100) / 100);
        },
        [setFieldValue]
    );

    // Note: we intentionally DO NOT hide the rate control when the currency is
    // local. Users want to keep the Change Rate button available even for a
    // local-currency document (the stored `rate` is still the object↔local rate
    // used to derive amountObj). The auto-fetch effects above already skip the
    // local case, so nothing is auto-overwritten — the user drives it manually.
    return (
        <Box>
            {autoLabel && <Box sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>{autoLabel}</Box>}
            <Grid container spacing={1} alignItems="flex-end">
                {showRateInput && (
                    <Grid item xs>
                        <TypedRenderInput
                            type="number"
                            label={`Rate ${localCurrencySymbol} → ${rateQuoteSymbol}`}
                            placeholder="0"
                            field={{
                                name: 'rate',
                                value: Math.round((values.rate || 0) * 10000) / 10000 // display 4 decimals
                            }}
                            inputProps={{ step: 0.0001 }}
                            handleChangeCallback={(ev: any) => {
                                const newRate = Number(ev.target.value);
                                if (!isNaN(newRate) && newRate > 0) {
                                    isRateCustomizedRef.current = true;
                                    reconvertAutoLocalAmount(newRate);
                                    setFieldValue('rate', roundRate(newRate)); // store 6 decimals
                                }
                            }}
                            variant="outlined"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            disabled={disabled}
                        />
                    </Grid>
                )}
                <Grid item xs>
                    <Tooltip title={disableRateDate ? rateDateDisabledTooltip : ''} placement="top" arrow>
                        <span style={{ display: 'block' }}>
                            <TypedRenderInput
                                type="date"
                                label="Rate Date"
                                field={{
                                    name: 'rateDate',
                                    value: rateDate,
                                    onChange: () => {},
                                    onBlur: () => {}
                                }}
                                form={{ errors: {}, touched: {}, setFieldValue }}
                                handleChangeCallback={(newDate: any) => {
                                    // Normalize Date|string → yyyy-mm-dd
                                    let nextStr: string | null = null;
                                    if (newDate instanceof Date) nextStr = toDateInputString(newDate);
                                    else if (typeof newDate === 'string' && newDate) nextStr = newDate;
                                    if (!nextStr) return;
                                    setRateDate(nextStr);
                                    if (!rateCurrencyId || String(rateCurrencyId) === String(localCurrency)) return;
                                    fetchRateForDate(rateCurrencyId, nextStr).then((newRate) => {
                                        if (rateDateRef.current !== nextStr) return;
                                        const resolvedRate = Number(newRate) || 0;
                                        if (!(resolvedRate > 0)) return;
                                        isRateCustomizedRef.current = false;
                                        reconvertAutoLocalAmount(resolvedRate);
                                        setFieldValue('rate', roundRate(resolvedRate));
                                    });
                                }}
                                autoOk
                                placeholder="__/__/____"
                                mask="__/__/____"
                                format="dd/MM/yyyy"
                                fullWidth
                                margin="none"
                                disabled={disabled || disableRateDate}
                            />
                        </span>
                    </Tooltip>
                </Grid>
                <Grid item sx={{ pb: '4px' }}>
                    <Button
                        variant="outlined"
                        size="small"
                        color="primary"
                        onClick={() => setChangeRateOpen(true)}
                        disabled={disabled}
                    >
                        Change Rate
                    </Button>
                </Grid>
            </Grid>
            {changeRateOpen && (
                <GeneralModal
                    open={true}
                    toggleOpen={() => setChangeRateOpen(false)}
                    reactComponent={ChangeRate}
                    componentProps={{
                        amount: values.amount,
                        documentCurrency,
                        setChangeRate: setChangeRateOpen,
                        setRate,
                        setAmount,
                        // The inherited source-document rate (doc→local). When present,
                        // the dialog defaults to it and shows Latest/Document buttons.
                        documentRate: originalRate
                    }}
                    title={title}
                    maxWidth="xs"
                    paperProps={{ sx: { width: 380, maxWidth: 380 } }}
                />
            )}
        </Box>
    );
};

export default memo(RateControlBox);
